import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Vault, Save, UploadCloud, Check, X, Wallet } from 'lucide-react';
import {
  adminDepositAddresses, adminSaveDepositAddress, adminDeposits,
  adminVerifyDeposit, adminRejectDeposit,
} from '../../api';

const statusStyle = {
  pending: 'text-[var(--orange)] bg-[#2a1a0e] border-[#4a2f14]',
  verified: 'text-[var(--green)] bg-[#0e2a20] border-[#1e5b45]',
  rejected: 'text-[var(--red)] bg-[#2a1010] border-[#5a2020]',
};

export default function CryptoVault({ token }) {
  const [addresses, setAddresses] = useState([]);
  const [deposits, setDeposits] = useState([]);
  const [edits, setEdits] = useState({});

  const load = useCallback(async () => {
    const [a, d] = await Promise.all([
      adminDepositAddresses(token).catch(() => []),
      adminDeposits(token).catch(() => []),
    ]);
    setAddresses(a);
    setDeposits(d);
    const map = {};
    a.forEach((x) => { map[`${x.coin}-${x.network}`] = { address: x.address, qr: x.qr || '' }; });
    setEdits(map);
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const key = (a) => `${a.coin}-${a.network}`;
  const setEdit = (k, patch) => setEdits((e) => ({ ...e, [k]: { ...e[k], ...patch } }));

  const onQr = (k) => (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    if (file.size > 500000) { toast.error('QR image too large (max 500KB)'); return; }
    const reader = new FileReader();
    reader.onload = () => setEdit(k, { qr: reader.result });
    reader.readAsDataURL(file);
  };

  const save = async (a) => {
    const k = key(a);
    const e = edits[k] || {};
    if (!e.address) { toast.error('Address is required'); return; }
    try {
      await adminSaveDepositAddress(token, { coin: a.coin, network: a.network, address: e.address, qr: e.qr || '' });
      toast.success(`${a.coin} ${a.network} address updated`);
      load();
    } catch { toast.error('Save failed'); }
  };

  const decide = async (id, verify) => {
    try {
      if (verify) await adminVerifyDeposit(token, id);
      else await adminRejectDeposit(token, id);
      toast.success(verify ? 'Deposit verified & credited' : 'Deposit rejected');
      load();
    } catch { toast.error('Action failed'); }
  };

  const pending = deposits.filter((d) => d.status === 'pending');

  return (
    <div className="space-y-6">
      {/* Deposit review */}
      <div className="rounded-xl bg-[var(--panel)] border border-[var(--border)] p-6">
        <div className="flex items-center gap-2">
          <Wallet size={16} className="text-[var(--orange)]" />
          <h3 className="font-display font-semibold text-[15px] text-white">PENDING DEPOSITS ({pending.length})</h3>
        </div>
        {deposits.length === 0 ? (
          <p className="text-[13px] text-[var(--muted)] mt-4 py-8 text-center border border-dashed border-[var(--border)] rounded-lg">No deposit requests yet.</p>
        ) : (
          <div className="mt-4 space-y-2 max-h-[360px] overflow-y-auto pr-1">
            {deposits.map((d) => (
              <div key={d.id} className="flex items-center justify-between gap-3 bg-[var(--bg)] border border-[var(--border-soft)] rounded-lg p-3 flex-wrap">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[15px] text-[var(--green)]">${d.amount.toLocaleString()}</span>
                    <span className="text-[12px] text-white">{d.coin} · {d.network}</span>
                    <span className={`text-[9px] font-mono px-2 py-0.5 rounded border ${statusStyle[d.status] || ''}`}>{d.status.toUpperCase()}</span>
                  </div>
                  <div className="text-[11px] font-mono text-[var(--muted-2)] mt-1">{d.userName} · {d.userEmail || d.userId.slice(0, 8)}</div>
                  <div className="text-[10.5px] font-mono text-[var(--muted-2)] mt-0.5 break-all">TX: {d.txHash}</div>
                </div>
                {d.status === 'pending' && (
                  <div className="flex items-center gap-2">
                    <button onClick={() => decide(d.id, false)} className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-[#5a2020] text-[var(--red)] text-[12px] hover:bg-[#2a1010] transition-colors"><X size={13} /> Reject</button>
                    <button onClick={() => decide(d.id, true)} className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-[var(--green)] text-[#052e20] text-[12px] font-semibold hover:bg-[#13b878] transition-colors"><Check size={13} /> Verify & Credit</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Address management */}
      <div className="rounded-xl bg-[var(--panel)] border border-[var(--border)] p-6">
        <div className="flex items-center gap-2">
          <Vault size={16} className="text-[var(--orange)]" />
          <h3 className="font-display font-semibold text-[15px] text-white">DEPOSIT ADDRESSES & QR</h3>
        </div>
        <p className="text-[12.5px] text-[var(--muted)] mt-2">Set the receiving wallet address and upload a QR image for each asset/network shown to investors.</p>
        <div className="mt-5 grid grid-cols-1 lg:grid-cols-2 gap-4">
          {addresses.map((a) => {
            const k = key(a);
            const e = edits[k] || {};
            return (
              <div key={k} className="rounded-lg bg-[var(--bg)] border border-[var(--border)] p-4">
                <div className="flex items-center gap-2 mb-3">
                  <span className="text-[13px] font-semibold text-white">{a.coin}</span>
                  <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-[var(--panel-2)] text-[var(--orange)] border border-[var(--border)]">{a.network}</span>
                </div>
                <div className="flex gap-3">
                  <div className="flex-1 space-y-2">
                    <input value={e.address || ''} onChange={(ev) => setEdit(k, { address: ev.target.value })} placeholder="Wallet address" className="w-full bg-[var(--panel)] border border-[var(--border)] rounded-lg px-3 py-2 font-mono text-[12px] text-white focus:outline-none focus:border-[var(--orange)]" />
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-1.5 px-3 py-2 rounded-md border border-[var(--border)] text-[11px] text-[var(--muted)] hover:text-white cursor-pointer transition-colors">
                        <UploadCloud size={13} /> Upload QR
                        <input type="file" accept="image/*" className="hidden" onChange={onQr(k)} />
                      </label>
                      <button onClick={() => save(a)} className="flex items-center gap-1.5 px-3 py-2 rounded-md bg-[var(--orange)] text-white text-[11px] font-semibold hover:bg-[var(--orange-bright)] transition-colors"><Save size={13} /> Save</button>
                    </div>
                  </div>
                  <div className="w-16 h-16 rounded-lg bg-white flex items-center justify-center overflow-hidden shrink-0">
                    <img src={e.qr && e.qr.length > 10 ? e.qr : `https://api.qrserver.com/v1/create-qr-code/?size=120x120&data=${encodeURIComponent(e.address || a.address)}`} alt="qr" className="w-full h-full object-contain" />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
